import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Radio,
  Chip,
  TextField,
} from "@mui/material";
import {
  ReceiptLong as ReceiptIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { UrlApi } from "../../../../../../../../services/apirest";
import ImputacionModal from "./ImputacionModal";

export default function SeleccionarRecibo({
  open,
  onClose,
  clienteData,
  getDocuments,
  onSelect,
  idNegocioCliente = 1,
}) {
  const [recibos, setRecibos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showImputacionesModal, setShowImputacionesModal] = useState(false);
  const [recibo, setRecibo] = useState(null);

  console.log("ONSELECT: ", onSelect);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);
    setSelectedId(null);
    setRecibos([]);

    fetch(
      `${UrlApi}/ctacte/recibos/saldo/${clienteData.id}/${idNegocioCliente}`,
    )
      .then((res) => {
        if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (data?.error) throw new Error(data.error);
        setRecibos(Array.isArray(data) ? data : []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [open, clienteData]);

  const handleChangeBusqueda = (e) => {
    const value = e.target.value.replace(/[^0-9-]/g, "");
    setBusqueda(value);
  };

  const handleOpenImputacion = () => {
    setShowImputacionesModal(true);
  };

  const findRecibo = (id) => {
    return recibos.find((r) => r.id === id) ?? null;
  };

  const handleConfirm = () => {
    const rec = findRecibo(selectedId);

    setRecibo(rec);
    setBusqueda("");

    if (!rec) return;

    onSelect?.(rec); // opcional

    handleOpenImputacion();
    // onClose();
  };

  const handleClose = () => {
    setSelectedId(null);
    setBusqueda("");
    onClose();
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2,
    }).format(amount ?? 0);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    const d = new Date(fechaStr);
    return isNaN(d)
      ? fechaStr
      : d.toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };
  // console.log("Recibos: ", recibos);

  const recibosFiltrados = useMemo(() => {
    return recibos.filter((r) => String(r.nro_recibo).startsWith(busqueda));
  }, [recibos, busqueda]);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#1976d2",
            color: "white",
            px: 3,
            py: 2,
            borderBottom: "1px solid #9cc2ff",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <ReceiptIcon sx={{ fontSize: 32, color: "white" }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Seleccionar Recibo para aplicar
              </Typography>
              {clienteData?.razon_social && (
                <Typography variant="body2" sx={{ color: "#c3c3c3" }}>
                  {clienteData.razon_social} - {clienteData?.cuit}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Cargando */}
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={6}
              gap={2}
            >
              <CircularProgress size={28} />
              <Typography color="text.secondary">
                Cargando recibos con saldo pendiente…
              </Typography>
            </Box>
          )}

          {/* Error */}
          {!loading && error && (
            <Alert severity="error" icon={<ErrorIcon />} sx={{ my: 2 }}>
              <Typography fontWeight="bold">
                No se pudieron cargar los recibos
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {/* Sin recibos */}
          {!loading && !error && recibos.length === 0 && (
            <Box textAlign="center" py={6}>
              <ReceiptIcon
                sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
              />
              <Typography color="text.secondary" variant="h6">
                No hay recibos con saldo pendiente
              </Typography>
            </Box>
          )}

          {/* Tabla */}
          {!loading && !error && recibos.length > 0 && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="body2" color="text.secondary">
                  Seleccioná un recibo para aplicar su saldo a las facturas
                  pendientes.
                </Typography>

                <TextField
                  onChange={handleChangeBusqueda}
                  value={busqueda}
                  size="small"
                  placeholder="Buscar por N° de recibo..."
                />
              </Box>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: "#E5E7EB",
                }}
              >
                {" "}
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      borderColor: "#F1F5F9",
                    },
                  }}
                >
                  {" "}
                  <TableHead>
                    <TableRow
                      sx={{
                        overflow: "hidden",
                        bgcolor: "#F8FAFC",
                        "& th": {
                          color: "#334155",
                          fontWeight: 700,
                          borderBottom: "1px solid #E2E8F0",
                        },
                      }}
                    >
                      {" "}
                      <TableCell padding="checkbox" />
                      <TableCell sx={{ fontWeight: 700 }}>Nro Recibo</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Fecha de</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        Saldo Pendiente
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recibosFiltrados.length > 0 ? (
                      recibosFiltrados.map((recibo) => {
                        const isSelected = selectedId === recibo.id;
                        return (
                          <TableRow
                            key={recibo.id}
                            hover
                            selected={isSelected}
                            onClick={() => setSelectedId(recibo.id)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell padding="checkbox">
                              <Radio
                                checked={isSelected}
                                onChange={() => setSelectedId(recibo.id)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                fontFamily="monospace"
                                fontWeight={600}
                              >
                                {recibo.nro_recibo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {formatFecha(recibo.fecha_de_pago)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(recibo.total)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={formatCurrency(recibo.saldo)}
                                size="small"
                                color="warning"
                                sx={{ fontWeight: 700 }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ py: 3 }}
                          >
                            No se encontraron recibos para la búsqueda.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: "#FAFAFA",
                        "& td": {
                          borderTop: "1px solid #E5E7EB",
                        },
                        overflow: "hidden",
                      }}
                    >
                      <TableCell colSpan={3}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="text.secondary"
                        >
                          Total de recibos
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>
                          {formatCurrency(
                            recibos.reduce(
                              (acc, r) => acc + Number(r.total || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="warning.main"
                        >
                          {formatCurrency(
                            recibos.reduce(
                              (acc, r) => acc + Number(r.saldo || 0),
                              0,
                            ),
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!selectedId}
          >
            Continuar con la aplicacion
          </Button>
        </DialogActions>
      </Dialog>
      <ImputacionModal
        open={showImputacionesModal}
        onClose={() => {
          setShowImputacionesModal(false);
          onClose();
        }}
        mode="post"
        cuit={clienteData.cuit}
        recibo={recibo}
        clienteData={clienteData}
        getDocuments={getDocuments}
        user={localStorage.getItem("user")}
        saldoDisponible={recibo?.saldo}
        // imputaciones={state.imputaciones}
        // onSetImputaciones={setImputaciones}
      />
    </>
  );
}
